import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import { Spinner } from "react-bootstrap";
import { SingleDatePickerPill } from "./ReservaShared";
import {
  SLOT_TAKEN,
  deadlineLabel,
  reasonMessage,
  remainingLabel,
  rescheduleCode,
  rescheduleErrorMessage,
  unwrap,
} from "./rescheduleModel";
import "../reservas.css";
import "./reschedule.css";

const DAY = "YYYY-MM-DD";

/**
 * Presentational reschedule dialog: day picker + slot list + confirm.
 * It owns no API: the caller injects
 *   loadOptions(date, professionalId) -> Promise<axios response of reschedule-options>
 *   onConfirm({ date, slotStart, professionalId }) -> Promise (rejects with the axios error)
 * The organizer variant passes `professionals` to add an optional professional select.
 */
const RescheduleModal = ({
  title = "Reagendar turno",
  summary = "",
  loadOptions,
  onConfirm,
  onClose,
  onDone,
  professionals = null,
  initialProfessionalId = "",
  initialDate,
}) => {
  const [date, setDate] = useState(initialDate || dayjs().format(DAY));
  const [professionalId, setProfessionalId] = useState(initialProfessionalId || "");
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [slotStart, setSlotStart] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const requestRef = useRef(0);
  const loadRef = useRef(loadOptions);
  loadRef.current = loadOptions;
  const minDate = useMemo(() => dayjs().startOf("day"), []);
  const maxDate = useMemo(() => minDate.add(365, "day"), [minDate]);

  const fetchOptions = useCallback(async () => {
    const id = ++requestRef.current;
    setLoading(true);
    setLoadError("");
    try {
      const info = unwrap(await loadRef.current(date, professionalId || undefined));
      if (id !== requestRef.current) return;
      setOptions(info || {});
      const slots = Array.isArray(info?.slots) ? info.slots : [];
      setSlotStart((current) => (slots.some((s) => s.slotStart === current) ? current : ""));
    } catch (e) {
      if (id !== requestRef.current) return;
      setOptions(null);
      setSlotStart("");
      setLoadError(e?.response?.data?.message || "No pudimos cargar los horarios. Probá de nuevo.");
    } finally {
      if (id === requestRef.current) setLoading(false);
    }
  }, [date, professionalId]);

  useEffect(() => {
    fetchOptions();
    return () => {
      requestRef.current += 1;
    };
  }, [fetchOptions]);

  const slots = Array.isArray(options?.slots) ? options.slots : [];
  const blocked = Boolean(options) && options.allowed === false;
  const hints = [remainingLabel(options?.remaining), deadlineLabel(options?.deadline)].filter(Boolean);

  const confirm = async () => {
    if (!slotStart || saving) return;
    setSaving(true);
    setError("");
    try {
      const res = await onConfirm({ date, slotStart, professionalId: professionalId || undefined });
      onDone?.(res);
    } catch (e) {
      setError(rescheduleErrorMessage(e));
      if (rescheduleCode(e) === SLOT_TAKEN) {
        setSlotStart("");
        fetchOptions();
      }
      setSaving(false);
    }
  };

  // Portal to <body>: callers render this inside animated/transformed modals, which would trap `position: fixed`.
  return createPortal(
    <div className="reservas-root">
      <div className="r-modal-back rs-back" style={{ zIndex: 9999 }} onClick={saving ? undefined : onClose}>
        <div
          className="r-modal rs-modal"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="rs-head">
            <h3 className="rs-title">{title}</h3>
            <button type="button" className="rs-close" aria-label="Cerrar" onClick={onClose} disabled={saving}>
              ×
            </button>
          </div>
          <div className="rs-body">
            {summary && <p className="rs-summary">{summary}</p>}

            {Array.isArray(professionals) && professionals.length > 0 && (
              <label className="rs-field">
                <span className="rs-label">Profesional</span>
                <select
                  className="rs-select"
                  aria-label="Profesional"
                  value={professionalId}
                  onChange={(e) => setProfessionalId(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Mismo profesional</option>
                  {professionals.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </label>
            )}

            <div className="rs-field">
              <span className="rs-label">Nueva fecha</span>
              <SingleDatePickerPill
                value={dayjs(date)}
                onChange={(d) => d && d.isValid() && setDate(d.format(DAY))}
                minDate={minDate}
                maxDate={maxDate}
                ariaLabel="Nueva fecha"
              />
            </div>

            {hints.length > 0 && <p className="rs-hint">{hints.join(" · ")}</p>}

            <div className="rs-field">
              <span className="rs-label">Horario</span>
              {loading ? (
                <div className="rs-note" role="status">
                  <Spinner animation="border" size="sm" /> Buscando horarios...
                </div>
              ) : loadError ? (
                <div className="rs-alert" role="alert">
                  {loadError}{" "}
                  <button type="button" className="rs-link" onClick={fetchOptions}>Reintentar</button>
                </div>
              ) : blocked ? (
                <div className="rs-alert" role="alert">{reasonMessage(options.reason)}</div>
              ) : slots.length === 0 ? (
                <p className="rs-note">No hay horarios libres ese día. Probá con otra fecha.</p>
              ) : (
                <div className="slots-grid rs-slots" role="group" aria-label="Horarios disponibles">
                  {slots.map((s) => (
                    <button
                      key={s.slotStart}
                      type="button"
                      className={`slot-btn${slotStart === s.slotStart ? " selected" : ""}`}
                      aria-pressed={slotStart === s.slotStart}
                      onClick={() => setSlotStart(s.slotStart)}
                      disabled={saving}
                    >
                      {s.slotStart}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {error && <div className="rs-alert" role="alert">{error}</div>}
          </div>
          <div className="rs-foot">
            <button type="button" className="r-btn-soft" onClick={onClose} disabled={saving}>Volver</button>
            <button type="button" className="r-btn-primary" onClick={confirm} disabled={!slotStart || saving || blocked}>
              {saving ? "Reagendando..." : "Confirmar nuevo horario"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RescheduleModal;
